import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { BackIcon } from '../icons/navigationIcons';

/** Match `ScrollableWindow` / `.scrollable-window` mobile breakpoints. */
const MOBILE_MAX_WIDTH = 768;

const styles = {
    root: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-md)',
        width: '100%',
        alignSelf: 'stretch',
    },
    rootMobile: {
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'flex-start',
        gap: 'var(--space-md)',
    },
    /** Mobile: back (left) and optional `action` (right) on one row above the title. */
    mobileTopRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 'var(--space-md)',
    },
    /** Title column: back control is absolutely placed to the left (out of flow) so the headline does not shift. */
    titleBlock: {
        position: 'relative',
        minWidth: 0,
        flex: '1 1 auto',
    },
    /** Stretches with the title block; flex-centers the tooltip wrapper (no transform on that node). */
    backOuter: {
        position: 'absolute',
        right: '100%',
        marginRight: 'var(--space-sm)',
        top: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        lineHeight: 0,
    },
    backOuterMobile: {
        position: 'static',
        marginLeft: '-16px',
        right: 'auto',
        marginRight: 0,
        top: 'auto',
        bottom: 'auto',
    },
    title: {
        minWidth: 0,
    },
    titleMobile: {
        width: '100%',
    },
    action: {
        flexShrink: 0,
    },
    actionMobileTop: {
        flexShrink: 0,
    },
};

/**
 * Case-study page title row. Pass `action` for optional right-aligned content (e.g. a text button with icon).
 * Omit `action` when no control is needed.
 */
const PageHeader = ({ title, action }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth <= MOBILE_MAX_WIDTH : false
    );

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX_WIDTH);
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const headerStyle = isMobile ? { ...styles.root, ...styles.rootMobile } : styles.root;
    const titleBlockStyle = styles.titleBlock;
    const backOuterStyle = isMobile ? { ...styles.backOuter, ...styles.backOuterMobile } : styles.backOuter;
    const titleStyle = isMobile ? { ...styles.title, ...styles.titleMobile } : styles.title;
    const actionStyle = styles.action;
    const actionMobileTopStyle = { ...styles.action, ...styles.actionMobileTop };

    const backButton = (
        <Button variant="icon" aria-label="Back" onClick={() => navigate('/')} icon={<BackIcon />} />
    );

    const backControl = (
        <div style={backOuterStyle}>
            {isMobile ? (
                backButton
            ) : (
                <div className="tooltip-wrapper tooltip-wrapper--header-back">
                    {backButton}
                    <span className="tooltip-text">Back</span>
                </div>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <header style={headerStyle}>
                <div style={styles.mobileTopRow}>
                    {backControl}
                    {action != null ? <div style={actionMobileTopStyle}>{action}</div> : null}
                </div>
                <div className="text-header-main" style={titleStyle}>
                    {title}
                </div>
            </header>
        );
    }

    return (
        <header style={headerStyle}>
            <div style={titleBlockStyle}>
                {backControl}
                <div className="text-header-main" style={titleStyle}>
                    {title}
                </div>
            </div>
            {action != null ? <div style={actionStyle}>{action}</div> : null}
        </header>
    );
};

export default PageHeader;
